import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { excluirRascunho, lerRascunho, salvarRascunho } from "../../utils/solicitacoes/rascunhoStorage";

/**
 * Estado do rascunho da Nova Solicitação para o processo aberto. O formulário se registra pelo
 * RascunhoContext (useRascunhoFormulario); esta página decide quando ler, salvar, restaurar e apagar.
 */
export function useRascunho(usuarioId, tipoProcesso) {
    const formularioRef = useRef(null);
    const [rascunhoPendente, setRascunhoPendente] = useState(null);
    const [ultimoSalvamento, setUltimoSalvamento] = useState(null);

    // Ao abrir um processo, oferece retomar o rascunho salvo (sem aplicar sozinho).
    useEffect(() => {
        setUltimoSalvamento(null);
        setRascunhoPendente(tipoProcesso ? lerRascunho(usuarioId, tipoProcesso) : null);
    }, [usuarioId, tipoProcesso]);

    const contexto = useMemo(() => ({
        registrar(formulario) {
            formularioRef.current = formulario;
            return () => {
                if (formularioRef.current === formulario) formularioRef.current = null;
            };
        },
    }), []);

    const salvar = useCallback(() => {
        if (!tipoProcesso || !formularioRef.current) return { ok: false, motivo: "formulário indisponível" };
        let dados;
        try {
            dados = formularioRef.current.obter();
        } catch {
            return { ok: false, motivo: "não foi possível ler os campos do formulário" };
        }
        const resultado = salvarRascunho(usuarioId, tipoProcesso, dados);
        if (resultado.ok) {
            setUltimoSalvamento(resultado.salvoEm);
            setRascunhoPendente(null); // o que está na tela já é o rascunho
        }
        return resultado;
    }, [usuarioId, tipoProcesso]);

    const restaurar = useCallback(() => {
        if (!rascunhoPendente || !formularioRef.current) return;
        try {
            formularioRef.current.aplicar(rascunhoPendente.dados);
            setUltimoSalvamento(rascunhoPendente.salvoEm);
            setRascunhoPendente(null);
            toast.info("Rascunho retomado. Confira os campos e anexe os arquivos novamente.");
        } catch {
            // Rascunho incompatível com o formulário atual — mantém salvo, não quebra a tela.
            toast.error("Não foi possível retomar este rascunho (o formulário pode ter mudado). Ele continua salvo.");
        }
    }, [rascunhoPendente]);

    const descartar = useCallback(() => {
        if (tipoProcesso) excluirRascunho(usuarioId, tipoProcesso);
        setRascunhoPendente(null);
        setUltimoSalvamento(null);
    }, [usuarioId, tipoProcesso]);

    /** Solicitação criada: o rascunho deixa de fazer sentido. */
    const limparAposCriar = useCallback(() => {
        if (tipoProcesso) excluirRascunho(usuarioId, tipoProcesso);
    }, [usuarioId, tipoProcesso]);

    return { contexto, rascunhoPendente, ultimoSalvamento, salvar, restaurar, descartar, limparAposCriar };
}
